// Country-level property-tax knowledge base loader + format helpers.
//
// Source: /data/property_tax.json (schema v1). The data is hand-curated
// JSON, ~15 high-confidence countries + ~15 low-confidence placeholders.
//
// Two display contracts:
//   1. UI chips / tooltips → `formatTierLabel`, `formatAnnualHeadline`
//   2. AI helper RAG-lite block → `buildAiContextBlock` returns a compact
//      object the LLM can quote verbatim (numbers + names + sources).
//
// Confidence rule (carry from PROPERTY_TAX_INTEGRATION.md): never show
// numeric values for `confidence: low` rows — they're placeholders.

import { useQuery } from '@tanstack/react-query';

export type PropertyTaxTier = 'low' | 'medium' | 'high';
export type PropertyTaxConfidence = 'low' | 'medium' | 'high';

export interface AnnualPropertyTax {
  name_local?: string | null;
  name_en?: string;
  /** Russian display name. `name_en` stays the sentinel for "no annual tax"
   *  (see `formatAnnual`) — never drop it in favour of this. */
  name_ru?: string;
  primary_residence_pct?: number | null;
  second_home_pct_min?: number | null;
  second_home_pct_max?: number | null;
  notes_en?: string;
  notes_ru?: string;
}

export interface PurchaseTax {
  primary_residence_pct_min?: number;
  primary_residence_pct_max?: number;
  second_home_pct_min?: number;
  second_home_pct_max?: number;
  notes_en?: string;
  notes_ru?: string;
}

export interface CapitalGainsTax {
  primary_residence_exempt?: boolean;
  primary_residence_exempt_after_years?: number;
  primary_residence_exempt_for_seniors?: boolean;
  primary_residence_rollover_exempt?: boolean;
  primary_residence_lifetime_exemption_eur?: number;
  second_home_holding_years_exempt?: number;
  rate_pct?: number | null;
  rate_pct_min?: number;
  rate_pct_max?: number;
  notes_en?: string;
  notes_ru?: string;
}

export interface WealthTaxRealEstate {
  applies: boolean;
  name_local?: string;
  name_en?: string;
  name_ru?: string;
  threshold_eur?: number;
  rate_pct_min?: number;
  rate_pct_max?: number;
  notes_en?: string;
  notes_ru?: string;
}

export interface ForeignerRegime {
  name_en?: string;
  name_ru?: string;
  notes_en?: string;
  notes_ru?: string;
}

export interface CountryPropertyTaxFull {
  annual_property_tax?: AnnualPropertyTax;
  purchase_tax?: PurchaseTax;
  capital_gains_tax?: CapitalGainsTax;
  wealth_tax_real_estate?: WealthTaxRealEstate;
  foreigner_regime?: ForeignerRegime | null;
  tier: PropertyTaxTier;
  confidence: PropertyTaxConfidence;
  last_updated?: string;
}

export interface CountryPropertyTaxPlaceholder {
  $confidence_note?: string;
  tier: PropertyTaxTier;
  confidence: 'low';
  last_updated?: string;
}

export type CountryPropertyTax = CountryPropertyTaxFull | CountryPropertyTaxPlaceholder;

export interface PropertyTaxFile {
  version: string;
  generated: string;
  schema_version: number;
  $comment?: string;
  sources?: Array<{ id: string; url: string; note: string }>;
  countries: Record<string, CountryPropertyTax>;
}

const LATEST_SCHEMA_VERSION = 1;

const QUERY_KEY = ['property-tax'] as const;

export function usePropertyTax() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<PropertyTaxFile> => {
      const res = await fetch('/data/property_tax.json');
      if (!res.ok) throw new Error(`property_tax.json: ${res.status}`);
      const data = (await res.json()) as PropertyTaxFile;
      if (data.schema_version > LATEST_SCHEMA_VERSION) {
        // Future schema version — refuse to render against stale loader. The
        // user gets the "no data" fallback rather than mis-rendered fields.
        throw new Error(
          `property_tax.json schema_version ${data.schema_version} > supported ${LATEST_SCHEMA_VERSION}`,
        );
      }
      return data;
    },
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: Number.POSITIVE_INFINITY,
  });
}

export function getCountryTax(
  file: PropertyTaxFile | undefined | null,
  countryCode: string,
): CountryPropertyTax | null {
  if (!file) return null;
  return file.countries[countryCode.toUpperCase()] ?? null;
}

export function isHighConfidence(
  entry: CountryPropertyTax | null,
): entry is CountryPropertyTaxFull {
  if (!entry) return false;
  return entry.confidence !== 'low' && 'annual_property_tax' in entry;
}

const TIER_LABEL_EN: Record<PropertyTaxTier, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};
const TIER_LABEL_RU: Record<PropertyTaxTier, string> = {
  low: 'низкие',
  medium: 'средние',
  high: 'высокие',
};

export function formatTierLabel(tier: PropertyTaxTier, lang: 'en' | 'ru'): string {
  return lang === 'ru' ? TIER_LABEL_RU[tier] : TIER_LABEL_EN[tier];
}

function rangeString(min?: number | null, max?: number | null): string | null {
  if (min == null && max == null) return null;
  if (min == null) return `${max}%`;
  if (max == null) return `${min}%`;
  if (min === max) return `${min}%`;
  return `${min}–${max}%`;
}

/**
 * Headline string for the annual-ownership tax — short enough for a chip
 * tooltip. Returns null when the entry is a placeholder or there's no
 * numeric data.
 *
 *   "IMU 0.5–1.06% on second homes (primary exempt)"
 *   "IBI 0.4–1.1% of cadastral value"
 */
export function formatAnnualHeadline(
  entry: CountryPropertyTax | null,
  lang: 'en' | 'ru',
): string | null {
  if (!isHighConfidence(entry)) return null;
  const ap = entry.annual_property_tax;
  if (!ap) return null;
  const name = ap.name_local ?? ap.name_en;
  // Sentinel: "None" / "Abolished*" or all-zero rates mean there is no annual
  // tax. Must short-circuit BEFORE the numeric branches — rangeString(0,0)
  // returns truthy '0%', which would emit "None 0% on second homes · primary
  // exempt" for AE/MT/MU/CY.
  const noAnnualTax =
    ap.name_en === 'None' ||
    /^Abolished/i.test(ap.name_en ?? '') ||
    ((ap.second_home_pct_min ?? null) === 0 &&
      (ap.second_home_pct_max ?? null) === 0 &&
      (ap.primary_residence_pct ?? 0) === 0);
  if (noAnnualTax) {
    return lang === 'ru'
      ? `${name ?? '—'} (нет ежегодного налога)`
      : `${name ?? '—'} (no annual tax)`;
  }
  const range = rangeString(ap.second_home_pct_min, ap.second_home_pct_max);
  const primaryExempt = ap.primary_residence_pct === 0;
  const parts: string[] = [];
  if (name) parts.push(name);
  if (range) {
    parts.push(
      lang === 'ru'
        ? `${range} (второе жильё)`
        : `${range} on second homes`,
    );
  }
  if (primaryExempt && range) {
    parts.push(
      lang === 'ru' ? '· основное жильё освобождено' : '· primary exempt',
    );
  }
  if (parts.length === 0) return null;
  return parts.join(' ');
}

/**
 * Headline string for the one-shot purchase tax. Falls back to "see notes"
 * when we only have prose. Always returns english/russian phrase.
 */
export function formatPurchaseHeadline(
  entry: CountryPropertyTax | null,
  lang: 'en' | 'ru',
): string | null {
  if (!isHighConfidence(entry)) return null;
  const pt = entry.purchase_tax;
  if (!pt) return null;
  const primary = rangeString(pt.primary_residence_pct_min, pt.primary_residence_pct_max);
  const second = rangeString(pt.second_home_pct_min, pt.second_home_pct_max);
  if (primary && second && primary === second) {
    return lang === 'ru' ? `Покупка: ${primary}` : `Purchase: ${primary}`;
  }
  if (primary && second) {
    return lang === 'ru'
      ? `Покупка: ${primary} (основное) / ${second} (второе)`
      : `Purchase: ${primary} (primary) / ${second} (second)`;
  }
  return null;
}

/**
 * Compact tooltip body — combines headline + 1-2 supporting facts. Stays
 * under ~140 chars so it works in a `title=""` attribute or popover.
 */
export function buildTooltipBody(
  entry: CountryPropertyTax | null,
  lang: 'en' | 'ru',
): string | null {
  if (!entry) return null;
  if (!isHighConfidence(entry)) {
    return lang === 'ru'
      ? 'Данные по этой стране в работе — точные ставки спросите у помощника или консультанта.'
      : 'Data pending for this country — ask the helper or a consultant for current rates.';
  }
  const lines: string[] = [];
  const annual = formatAnnualHeadline(entry, lang);
  if (annual) lines.push(annual);
  const purchase = formatPurchaseHeadline(entry, lang);
  if (purchase) lines.push(purchase);
  if (entry.wealth_tax_real_estate?.applies) {
    const w = entry.wealth_tax_real_estate;
    const name = w.name_local ?? w.name_en ?? 'Wealth tax';
    const threshold = w.threshold_eur ? `€${(w.threshold_eur / 1000).toFixed(0)}k+` : '';
    lines.push(
      lang === 'ru'
        ? `Налог на недвижимое богатство (${name}) ${threshold}`
        : `Wealth tax on RE (${name}) ${threshold}`,
    );
  }
  if (entry.foreigner_regime?.name_en) {
    lines.push(
      lang === 'ru'
        ? `Спецрежим: ${entry.foreigner_regime.name_en}`
        : `Regime: ${entry.foreigner_regime.name_en}`,
    );
  }
  return lines.join(' · ');
}

/**
 * Compact object to inject into the AI helper CONTEXT block. Stays under
 * ~600 bytes per country so we don't blow past the context budget. The LLM
 * reads `tier`, `annual_headline`, `purchase_headline`, `wealth_tax`,
 * `foreigner_regime` and quotes them verbatim — system prompt forbids
 * fabricating numbers, so this is the SAFE source.
 */
export interface PropertyTaxAiBlock {
  country_code: string;
  tier: PropertyTaxTier;
  confidence: PropertyTaxConfidence;
  annual_headline: string | null;
  purchase_headline: string | null;
  wealth_tax: string | null;
  foreigner_regime: string | null;
  notes: string | null;
}

export function buildAiContextBlock(
  file: PropertyTaxFile | undefined | null,
  countryCode: string,
  lang: 'en' | 'ru',
): PropertyTaxAiBlock | null {
  const entry = getCountryTax(file, countryCode);
  if (!entry) return null;
  const tier = entry.tier;
  const confidence = entry.confidence;
  if (!isHighConfidence(entry)) {
    return {
      country_code: countryCode.toUpperCase(),
      tier,
      confidence,
      annual_headline: null,
      purchase_headline: null,
      wealth_tax: null,
      foreigner_regime: null,
      notes: lang === 'ru'
        ? 'Точные ставки по этой стране ещё уточняются — не называй конкретные числа.'
        : 'Specific rates for this country are still being verified — do not state exact numbers.',
    };
  }
  const ap = entry.annual_property_tax;
  const notes = ap ? (lang === 'ru' ? ap.notes_ru ?? ap.notes_en ?? null : ap.notes_en ?? null) : null;
  let wealth: string | null = null;
  if (entry.wealth_tax_real_estate?.applies) {
    const w = entry.wealth_tax_real_estate;
    const name = w.name_local ?? w.name_en ?? 'Wealth tax';
    const range = rangeString(w.rate_pct_min, w.rate_pct_max);
    const threshold = w.threshold_eur ? `€${(w.threshold_eur / 1000).toFixed(0)}k threshold` : '';
    wealth = [name, range, threshold].filter(Boolean).join(' · ');
  }
  return {
    country_code: countryCode.toUpperCase(),
    tier,
    confidence,
    annual_headline: formatAnnualHeadline(entry, lang),
    purchase_headline: formatPurchaseHeadline(entry, lang),
    wealth_tax: wealth,
    foreigner_regime: entry.foreigner_regime?.name_en ?? null,
    notes: notes ?? null,
  };
}
